import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { DeskView } from '../desk/DeskView';
import { MapView } from '../mapview/MapView';
import { TimelineView } from '../timeline/TimelineView';
import { ReportsArea } from '../reports/ReportsArea';
import { setupApi, type SetupState } from '../settings/api';
import { SettingsArea } from '../settings/SettingsArea';
import { onSetupChanged } from '../settings/setup/events';
import { shouldOpenGuide } from '../settings/setup/items';
import { AlarmBanner } from '../alarms/AlarmBanner';
import { DeviceRequestBanner } from '../devices/DeviceRequestBanner';
import { WaitingNotice } from '../places/WaitingNotice';
import { DispatchProvider } from './DispatchProvider';
import { usePreferences } from './preferencesContext';
import { SoftphoneProvider } from './SoftphoneProvider';
import { TopBar, type Area } from './TopBar';
import { defaultView, rememberDefault, viewForKey, VIEWS, type View } from './views';

/**
 * The release notes, as a file of their own. Loaded only when an administrator opens them:
 * their pictures show every screen of the system, and a dispatcher's browser never fetches
 * them — see `whatsnew/seen.ts`.
 */
const WhatsNewArea = lazy(async () => ({
  default: (await import('../whatsnew/WhatsNewArea')).WhatsNewArea,
}));

/** Which area a path belongs to, and which view is showing if it is a view. */
function whereAmI(pathname: string): { area: Area; view?: View } {
  const first = pathname.split('/')[1] ?? '';

  if (VIEWS.includes(first as View)) return { area: 'dispatch', view: first as View };
  if (first === 'settings') return { area: 'settings' };
  if (first === 'reports') return { area: 'reports' };
  if (first === 'whats-new') return { area: 'whatsNew' };

  return { area: 'dispatch' };
}

/**
 * Everything a signed-in member of staff sees: the bar, the routes under it, and the two things
 * that must outlive a route change — the telephone and the dispatch state.
 *
 * The views are routes rather than tabs so that a workstation can be left on one, a link can
 * be sent to one, and the browser's own back button means what it looks like it means.
 */
export function ConsoleShell() {
  const navigate = useNavigate();
  const location = useLocation();

  const [pinned, setPinned] = useState<View>(() => defaultView(globalThis.localStorage));

  const { access, save: savePreference } = usePreferences();

  const { area, view } = whereAmI(location.pathname);

  /**
   * How far the firm's setup has got, for the chip in the bar — and, once, for sending a
   * brand-new administrator to the guide rather than the desk.
   *
   * Null for anyone who is not an administrator, who is never asked: the access answer at
   * sign-in already said the settings are not theirs, and asking anyway would only put a
   * refused request in the browser's console. Asked again when a screen says the setup
   * changed and when the person comes back to dispatch from a settings page, so a tariff
   * added on the Prices page moves the number without a reload.
   */
  const [setup, setSetup] = useState<SetupState | null>(null);

  const refreshSetup = useCallback(() => {
    if (!access.settings) return;

    setupApi
      .state()
      .then(setSetup)
      .catch(() => setSetup(null));
  }, [access.settings]);

  useEffect(() => {
    refreshSetup();

    return onSetupChanged(refreshSetup);
  }, [refreshSetup]);

  useEffect(() => {
    if (area === 'dispatch') refreshSetup();
  }, [area, refreshSetup]);

  // Only while the guide has never been seen. The guide records a stage the moment it is
  // started or dismissed, so this fires on a firm's first administrator's first arrival
  // and then never again — and never for a dispatcher, who is never asked.
  const sentToGuide = useRef(false);

  useEffect(() => {
    if (setup === null || sentToGuide.current || !shouldOpenGuide(setup)) return;

    sentToGuide.current = true;
    void navigate('/setup', { replace: true });
  }, [setup, navigate]);

  // Registered once on the window rather than per view, so that the keys work wherever the
  // focus happens to be — and refused wherever a digit means a digit, which `viewForKey`
  // decides from the event's own target.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;

      const next = viewForKey(event.key, event.target);

      if (next === null) return;

      event.preventDefault();
      void navigate(`/${next}`);
    }

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const pin = useCallback(() => {
    if (view === undefined) return;

    rememberDefault(globalThis.localStorage, view);
    setPinned(view);
    savePreference({ defaultView: view });
  }, [view, savePreference]);

  return (
    <SoftphoneProvider>
      <DispatchProvider>
        <div className="flex flex-col h-screen overflow-hidden">
          <TopBar
            area={area}
            view={view}
            pinned={pinned}
            onPin={pin}
            setupPercent={setup?.percent ?? null}
          />

          {/* Above every view, because an alarm is the one thing that must not depend on
              which screen a dispatcher happens to have open. */}
          <AlarmBanner />

          {/* The firm's addresses waiting for the administrator (#420): said at sign-in. */}
          <WaitingNotice />

          {/* A phone asking to be a driver's service device, with somebody waiting on it. */}
          <DeviceRequestBanner />

          <main className="flex-1 min-h-0 overflow-hidden flex flex-col">
            <Routes>
              <Route path="/" element={<Navigate to={`/${pinned}`} replace />} />
              <Route path="/desk" element={<DeskView />} />
              <Route path="/map" element={<MapView />} />
              <Route path="/timeline" element={<TimelineView />} />

              {/* Mounted only for somebody they belong to. A dispatcher who types the address
                  falls through to the catch-all below and lands on their pinned view, rather
                  than on a page whose every request is refused. */}
              {access.settings && (
                <>
                  <Route path="/settings" element={<Navigate to="/settings/dispatch" replace />} />
                  <Route path="/settings/:section" element={<SettingsArea />} />
                </>
              )}

              {access.reports && (
                <>
                  <Route path="/reports" element={<Navigate to="/reports/shift-book" replace />} />
                  <Route path="/reports/:report" element={<ReportsArea />} />
                </>
              )}

              {access.whatsNew && (
                <>
                  <Route path="/whats-new" element={<Notes />} />
                  <Route path="/whats-new/:release" element={<Notes />} />
                </>
              )}

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      </DispatchProvider>
    </SoftphoneProvider>
  );
}

/** The notes, with the console's ordinary word while their file arrives. */
function Notes() {
  const { t } = useTranslation();

  return (
    <Suspense fallback={<p className="text-ink-3 text-center p-10">{t('app.loading')}</p>}>
      <WhatsNewArea />
    </Suspense>
  );
}
